/* globals process:false */

import VueRouter from 'vue-router';


export default class Router {


  constructor(Vue, {routes, router: routerOptions}) {
    this.options = routerOptions;
    this.store = Vue.store;

    Vue.use(VueRouter);

    const router = new VueRouter({
      mode: 'history',
      base: process.env.BASE_URL,
      routes,
      scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
          return savedPosition;
        } else {
          return {x: 0, y: 0};
        }
      }
    });

    let user = null;
    router.beforeEach(async (to, from, next) => {
      user = await this._getUser(); // Update user.
      if (user && user.loggedIn && !user.accountId) {
        // eslint-disable-next-line no-console
        console.warn([
          '"accountId" missing in user data (/session).',
          'Needed to properly update route if user changes.'
        ].join(' '));
      }
      this._handleRoute(user, to, next);
    });

    // Watch for user changes.
    const {store} = this;
    store.watch(() => store.getters['session/getUser'], (user_updated) => {
      // Check if user has been changed.
      if (!user || user_updated && user_updated.accountId === user.accountId) {
        return;
      }

      this._handleRoute(user_updated, router.currentRoute, (nextArg) => {
        if (nextArg) {
          const route = typeof nextArg === 'string' ? {path: nextArg} : nextArg;
          router.push(route);
        } else {
          /* NOOP - Stay at current route. */
        }
      });
    });

    Vue.router = router;

    return router;
  }


  static checkPermission(routes, userRoles = []) {
    // Check routes from bottom to top (nested to parent).
    for (let i = routes.length - 1; i >= 0 ; --i) {
      const {permissions} = routes[i].meta;

      // Permissions set - check if user has at least one role.
      if (permissions && permissions.length) {
        return permissions.some((permission) => userRoles.includes(permission));
      }

      // Top most route has no permissions - grant access.
      if (i === 0) {
        return true;
      }

      // Route has no permissions - check next/parent route.
    }
  }


  _handleRoute(user, to, next) {
    // Redirect to root/landing page if page not found and user not logged in.
    if (to.name === '404' && !user.loggedIn) {
      return next('/');
    }

    // Redirect to user start page or root page if logged in.
    if (to.path === '/' && user.loggedIn) {
      const loginRedir = sessionStorage.getItem('login_redir');
      if (loginRedir) {
        sessionStorage.removeItem('login_redir');
      }
      return next(loginRedir || this.options.userStartPage || '/home');
    }

    const access = Router.checkPermission(to.matched, user.roles);
    if (access) {
      return next();
    }

    // No access
    if (!user.loggedIn) {
      sessionStorage.setItem('login_redir', to.fullPath); // Store path for redirection after login.
      return next(to.path !== '/' ? '/' : undefined);
    }

    next({
      name: 'error',
      params: {
        status: 403,
        title: 'error.403.title',
        message: 'error.403.message',
        debug: {
          name: to.name,
          meta: to.meta,
          path: to.path,
          hash: to.hash,
          query: to.query,
          params: to.params
        }
      }
    });
  }


  async _getUser() {
    const {store} = this;
    const user = store.getters['session/getUser'];

    return new Promise((resolve) => {
      if (user) {
        return resolve(user);
      }

      // Show loading screen if getting user data lasts longer than 500 miliseconds.
      let loadingTimeout = setTimeout(() => store.dispatch('loading', true), 500);

      const unwatch = store.watch(() => store.getters['session/getUser'], (user_updated) => {
        unwatch();
        clearTimeout(loadingTimeout);
        store.dispatch('loading', false);

        resolve(user_updated);
      });
    });
  }
}
